import React, { useState, useEffect, useContext, useRef } from 'react';
import { Box, Typography, Stepper, Step, StepLabel, Button, TextField, FormControl, InputLabel, Select, MenuItem, Checkbox, FormControlLabel, FormGroup, Grid, Card, CardContent, Chip, Alert, CircularProgress, RadioGroup, Radio, FormLabel, Divider, IconButton, Fab } from '@mui/material';
import { Save, Send, ArrowBack, ArrowForward, LocationOn, CheckCircle, CloudUpload, PictureAsPdf } from '@mui/icons-material';
import axios from 'axios';
import { AuthContext } from '../context/AuthContext';
import { useSearchParams, useNavigate } from 'react-router-dom';

const API = '/api';

const INDIAN_STATES = [
  'Andhra Pradesh','Arunachal Pradesh','Assam','Bihar','Chhattisgarh',
  'Goa','Gujarat','Haryana','Himachal Pradesh','Jharkhand','Karnataka',
  'Kerala','Madhya Pradesh','Maharashtra','Manipur','Meghalaya','Mizoram',
  'Nagaland','Odisha','Punjab','Rajasthan','Sikkim','Tamil Nadu','Telangana',
  'Tripura','Uttar Pradesh','Uttarakhand','West Bengal',
  '── Union Territories ──',
  'Andaman & Nicobar Islands','Chandigarh',
  'Dadra & Nagar Haveli and Daman & Diu','Delhi (NCT)',
  'Jammu & Kashmir','Ladakh','Lakshadweep','Puducherry'
];

const STEPS = ['Customer','Electricity','Site','System','Financial','Documents','Load','Submit'];

const LOAD_ITEMS = ['LED Lights','Ceiling Fans','Air Conditioner (1.5T)','Refrigerator','Washing Machine','Television','Water Pump / Motor','Geyser / Water Heater','Computer / Laptop','Microwave / Oven'];

const DOCS = [
  'Aadhar Card (Front & Back)','PAN Card','Voter ID Card','Property Ownership Document',
  'Khata Certificate','Latest Electricity Bill (3 months)','DISCOM NOC for Net Metering',
  'Bank Passbook / Statement (6 months)','Subsidy Registration Confirmation',
  'Site Photographs (4 directions)','Electrical Panel Photograph','GPS Screenshot',
  'Cancelled Cheque','Owner NOC (if rented)',
];

const SHADING = ['No shading (ideal)','Partial morning shade','Partial evening shade','Trees / Vegetation nearby','Adjacent buildings / walls','Overhead lines / cables'];

const RGFField = ({label, field, type='text', required=false, half=false, maxLen=200, form, set}) => (
  <Grid item xs={12} sm={half?6:12}>
    <TextField fullWidth size="small" label={label} type={type}
      value={form[field]||''}
      onChange={e=>set(field, e.target.value)}
      required={required}
      InputLabelProps={type==='date'?{shrink:true}:undefined}
      inputProps={{maxLength:maxLen}}
      sx={{'& .MuiOutlinedInput-root':{borderRadius:2}}}/>
  </Grid>
);

const RGFSelect = ({label, field, options, half=false, form, set}) => (
  <Grid item xs={12} sm={half?6:12}>
    <FormControl fullWidth size="small">
      <InputLabel>{label}</InputLabel>
      <Select value={form[field]||''} label={label} onChange={e=>set(field,e.target.value)} sx={{borderRadius:2}}>
        {options.map(o=><MenuItem key={o} value={o}>{o}</MenuItem>)}
      </Select>
    </FormControl>
  </Grid>
);

export default function SolarRGF() {
  const { user } = useContext(AuthContext);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const appointmentId = searchParams.get('appointment');
  const rgfId = searchParams.get('id');

  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [rgf, setRgf] = useState(null);
  const [apptInfo, setApptInfo] = useState(null);

  const [form, setForm] = useState({
    // S1
    customerName:'', dob:'', fathersName:'', gender:'', permanentAddress:'',
    district:'', state:'', pinCode:'', mobileAlt:'', email:'', whatsapp:'',
    aadhar:'', pan:'', ownershipStatus:'',
    // S2
    kno:'', consumerNo:'', meterNoOld:'', smartMeterNo:'', discom:'',
    divisionName:'', subdivisionName:'', connectionType:'', consumerCategory:'',
    sanctionedLoad:'', connectedLoad:'', maxDemand:'', avgMonthlyConsumption:'',
    avgMonthlyBill:'', currentTariff:'', last3MonthsAvgBill:'', highestBillMonth:'',
    // S3
    siteAddressSame:true, siteAddress:'', siteDistrict:'', siteState:'', sitePinCode:'',
    gpsLat:'', gpsLng:'', landmark:'', siteContact:'', rooftopType:'', rooftopCondition:'',
    availableRooftopArea:'', shadowFreeArea:'', rooftopHeight:'', rooftopOrientation:'',
    tiltAngle:'', obstructions:'', distanceFromPanel:'', earthingAvailable:'',
    waterSupplyAvailable:'', shadingFactors:[], siteRemarks:'',
    // S4
    systemType:'', proposedCapacity:'', preferredCapacity:'', panelCount:'',
    panelWattage:'', panelTechnology:'', inverterType:'', inverterBrand:'',
    panelBrand:'', batteryType:'', backupDuration:'', batteryCapacity:'',
    mountingType:'', expectedGeneration:'', targetBillReduction:'', paybackPeriod:'',
    monitoringRequired:false,
    // S5
    installationCategory:'', subsidy:'', totalCost:'', subsidyAmount:'',
    downPayment:'', loanAmount:'', paymentMode:'', preferredBank:'',
    bankAccount:'', ifsc:'', bankBranch:'', monthlyIncome:'', annualIncome:'',
    bplCardNo:'', rationCardNo:'',
    // S6
    documentsCollected:[],
    // S7
    loadItems: LOAD_ITEMS.map(a=>({appliance:a,qty:'',wattage:'',hoursPerDay:'',daysPerMonth:''})),
    totalConnectedLoad:'', totalMonthlyConsumption:'', peakDemandPeriod:'',
    // S8
    customerConsent:false, customerSignatureDate: new Date().toISOString().slice(0,10),
  });

  const hdr = { headers: { Authorization: 'Bearer ' + localStorage.getItem('token') } };

  useEffect(() => {
    // Load appointment info
    if (appointmentId) {
      axios.get(API+'/appointments', hdr).then(r => {
        const appts = r.data.appointments || [];
        const appt = appts.find(a => a._id === appointmentId);
        if (appt) {
          setApptInfo(appt);
          const lead = appt.lead || {};
          const name = lead.name || ((lead.firstName||'')+' '+(lead.lastName||'')).trim();
          // Auto-fill from lead and master data
          setForm(f => ({
            ...f,
            customerName: name,
            permanentAddress: lead.address || '',
            state: lead.state || '',
            district: lead.city || lead.district || '',
            kno: lead.kno || '',
          }));
          // Fetch master data for more fields
          if (lead.phone) {
            axios.get(API+'/master-data?phone='+lead.phone, hdr).then(md => {
              const rec = md.data.records?.[0] || md.data.data?.[0];
              if (rec) {
                setForm(f => ({
                  ...f,
                  kno: rec.kno || f.kno,
                  subdivisionName: rec.subdivision || '',
                  divisionName: rec.division || '',
                  sanctionedLoad: rec.connectedLoad || '',
                  avgMonthlyConsumption: rec.averageKW || '',
                  avgMonthlyBill: rec.monthlyBill || '',
                  state: rec.state || f.state,
                  district: rec.city || f.district,
                  smartMeterNo: rec.newMeterNo || '',
                  connectedLoad: rec.connectedLoad || '',
                }));
              }
            }).catch(()=>{});
          }
        }
      }).catch(()=>{});
    }
    // Load existing RGF
    const loadId = rgfId || null;
    if (loadId) {
      axios.get(API+'/solar-rgf/'+loadId, hdr).then(r => {
        if (r.data.rgf) { setRgf(r.data.rgf); setForm(f=>({...f,...r.data.rgf})); }
      }).catch(()=>{});
    } else if (appointmentId) {
      axios.get(API+'/solar-rgf/appointment/'+appointmentId, hdr).then(r => {
        if (r.data.rgf) { setRgf(r.data.rgf); setForm(f=>({...f,...r.data.rgf})); }
      }).catch(()=>{});
    }
  }, [appointmentId, rgfId]);

  const set = (field, value) => setForm(f => ({...f, [field]: value}));
  const toggleArr = (field, val) => setForm(f => {
    const arr = f[field] || [];
    return {...f, [field]: arr.includes(val) ? arr.filter(x=>x!==val) : [...arr,val]};
  });

  const getLocation = () => {
    if (!navigator.geolocation) {
      alert('GPS not supported. Please enter coordinates manually from Google Maps.');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      pos => {
        set('gpsLat', pos.coords.latitude.toFixed(6));
        set('gpsLng', pos.coords.longitude.toFixed(6));
      },
      err => {
        if (err.code === 1) {
          alert('Location access denied.\n\nTo fix:\n1. Click the lock icon in browser address bar\n2. Allow Location\n3. Refresh and try again\n\nOr enter GPS coordinates manually from Google Maps.');
        } else {
          alert('Could not get GPS location. Please enter coordinates manually.');
        }
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  const saveDraft = async () => {
    try {
      setSaving(true); setError('');
      if (rgf?._id) {
        await axios.put(API+'/solar-rgf/'+rgf._id, {...form, appointmentId}, hdr);
      } else {
        const r = await axios.post(API+'/solar-rgf', {...form, appointmentId}, hdr);
        setRgf(r.data.rgf);
      }
      setSuccess('Draft saved!'); setTimeout(()=>setSuccess(''),3000);
    } catch(e) { setError(e.response?.data?.message||'Failed to save'); }
    finally { setSaving(false); }
  };

  const submitForm = async () => {
    if (!form.customerConsent) return setError('Customer consent is required before submission');
    try {
      setSubmitting(true); setError('');
      const payload = {...form, customerConsent:true, appointmentId};
      if (rgf?._id) {
        await axios.put(API+'/solar-rgf/'+rgf._id, payload, hdr);
      } else {
        await axios.post(API+'/solar-rgf', payload, hdr);
      }
      setSuccess('Form submitted successfully! Forwarded to ERP & Installation team.');
      setTimeout(()=>navigate('/appointments'), 2500);
    } catch(e) { setError(e.response?.data?.message||'Failed to submit'); }
    finally { setSubmitting(false); }
  };





  const SectionTitle = ({title}) => (
    <Grid item xs={12}>
      <Box sx={{bgcolor:'#003E6F',color:'white',px:2,py:1,borderRadius:1.5,mb:1}}>
        <Typography fontWeight={700} fontSize={13}>{title}</Typography>
      </Box>
    </Grid>
  );

  const steps = [
    // Step 0: Customer
    <Grid container spacing={1.5}>
      <SectionTitle title="Section 1 — Customer Identification & Contact"/>
      {apptInfo && <Grid item xs={12}><Alert severity="info" sx={{py:0.5,fontSize:12}}>Appointment: {apptInfo.title} | {new Date(apptInfo.startTime).toLocaleDateString('en-IN')}</Alert></Grid>}
      <RGFField form={form} set={set} label="Customer Full Name *" field="customerName" required half/>
      <RGFField form={form} set={set} label="Date of Birth" field="dob" type="date" half/>
      <RGFField form={form} set={set} label="Father's / Husband's Name" field="fathersName" half/>
      <RGFSelect form={form} set={set} label="Gender" field="gender" options={['Male','Female','Other']} half/>
      <RGFField form={form} set={set} label="Permanent Address" field="permanentAddress"/>
      <RGFField form={form} set={set} label="District" field="district" half/>
      <Grid item xs={12} sm={6}><FormControl fullWidth size="small"><InputLabel>State</InputLabel><Select value={form.state||''} label="State" onChange={e=>set('state',e.target.value)} sx={{borderRadius:2}}>{INDIAN_STATES.map(s=><MenuItem key={s} value={s}>{s}</MenuItem>)}</Select></FormControl></Grid>
      <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="PIN Code" value={form.pinCode||''} onChange={e=>{if(/^\d{0,6}$/.test(e.target.value))set('pinCode',e.target.value)}} inputProps={{maxLength:6,pattern:'[0-9]*'}} helperText={form.pinCode&&form.pinCode.length!==6?'Must be 6 digits':''} error={!!(form.pinCode&&form.pinCode.length!==6)} sx={{'& .MuiOutlinedInput-root':{borderRadius:2}}}/></Grid>
      <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Mobile (Alternative)" value={form.mobileAlt||''} onChange={e=>{if(/^\d{0,10}$/.test(e.target.value))set('mobileAlt',e.target.value)}} inputProps={{maxLength:10}} sx={{'& .MuiOutlinedInput-root':{borderRadius:2}}}/></Grid>
      <RGFField form={form} set={set} label="Email Address" field="email" half/>
      <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="WhatsApp Number" value={form.whatsapp||''} onChange={e=>{if(/^\d{0,10}$/.test(e.target.value))set('whatsapp',e.target.value)}} inputProps={{maxLength:10}} helperText={form.whatsapp&&form.whatsapp.length!==10?'Must be 10 digits':''} error={!!(form.whatsapp&&form.whatsapp.length!==10)} sx={{'& .MuiOutlinedInput-root':{borderRadius:2}}}/></Grid>
      <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Aadhar Card Number" value={form.aadhar||''} onChange={e=>{if(/^\d{0,12}$/.test(e.target.value))set('aadhar',e.target.value)}} inputProps={{maxLength:12}} helperText={form.aadhar&&form.aadhar.length!==12?'Must be 12 digits':''} error={!!(form.aadhar&&form.aadhar.length!==12)} sx={{'& .MuiOutlinedInput-root':{borderRadius:2}}}/></Grid>
      <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="PAN Card Number" value={form.pan||''} onChange={e=>set('pan',e.target.value.toUpperCase().slice(0,10))} inputProps={{maxLength:10}} helperText={form.pan&&form.pan.length!==10?'Must be 10 characters':''} error={!!(form.pan&&form.pan.length!==10)} sx={{'& .MuiOutlinedInput-root':{borderRadius:2}}}/></Grid>
      <RGFSelect form={form} set={set} label="Ownership Status" field="ownershipStatus" options={['Owner / Self Occupied','Tenant / Rented','Joint Ownership','Government Allotted']}/>
    </Grid>,

    // Step 1: Electricity
    <Grid container spacing={1.5}>
      <SectionTitle title="Section 2 — Electricity Connection & Meter Details"/>
      <RGFField form={form} set={set} label="K-Number (KNO)" field="kno" half maxLen={15}/>
      <RGFField form={form} set={set} label="Consumer Account No" field="consumerNo" half maxLen={15}/>
      <RGFField form={form} set={set} label="Old Meter Number" field="meterNoOld" half maxLen={15}/>
      <RGFField form={form} set={set} label="Smart Meter Number (AMI)" field="smartMeterNo" half maxLen={15}/>
      <RGFField form={form} set={set} label="Electricity Board / DISCOM" field="discom" half maxLen={115}/>
      <RGFField form={form} set={set} label="Division Name" field="divisionName" half maxLen={115}/>
      <RGFField form={form} set={set} label="Subdivision Name" field="subdivisionName" half maxLen={115}/>
      <RGFSelect form={form} set={set} label="Connection Type" field="connectionType" options={['Single Phase (1PH)','Three Phase (3PH)','HT Connection','Industrial']} half/>
      <RGFSelect form={form} set={set} label="Consumer Category" field="consumerCategory" options={['BPL (Below Poverty Line)','LT Domestic','LT Commercial','LT Industrial / Agricultural']}/>
      <RGFField form={form} set={set} label="Sanctioned Load (kW)" field="sanctionedLoad" half maxLen={115}/>
      <RGFField form={form} set={set} label="Connected Load (kW)" field="connectedLoad" half maxLen={115}/>
      <RGFField form={form} set={set} label="Avg Monthly Consumption (kWh)" field="avgMonthlyConsumption" half maxLen={115}/>
      <RGFField form={form} set={set} label="Avg Monthly Bill (₹)" field="avgMonthlyBill" half maxLen={115}/>
      <RGFField form={form} set={set} label="Current Tariff Rate (₹/kWh)" field="currentTariff" half maxLen={115}/>
      <RGFField form={form} set={set} label="Last 3 Months Avg Bill (₹)" field="last3MonthsAvgBill" half maxLen={115}/>
    </Grid>,

    // Step 2: Site
    <Grid container spacing={1.5}>
      <SectionTitle title="Section 3 — Site & Rooftop Assessment"/>
      <Grid item xs={12}>
        <FormControlLabel control={<Checkbox checked={form.siteAddressSame} onChange={e=>set('siteAddressSame',e.target.checked)}/>}
          label={<Typography fontSize={13}>Installation site same as residential address</Typography>}/>
      </Grid>
      {!form.siteAddressSame && <>
        <RGFField form={form} set={set} label="Site Address" field="siteAddress"/>
        <RGFField form={form} set={set} label="Site District" field="siteDistrict" half/>
        <Grid item xs={12} sm={6}><FormControl fullWidth size="small"><InputLabel>Site State</InputLabel><Select value={form.siteState||''} label="Site State" onChange={e=>set('siteState',e.target.value)} sx={{borderRadius:2}}>{INDIAN_STATES.map(s=><MenuItem key={s} value={s}>{s}</MenuItem>)}</Select></FormControl></Grid>
        <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Site PIN Code" value={form.sitePinCode||''} onChange={e=>{if(/^\d{0,6}$/.test(e.target.value))set('sitePinCode',e.target.value)}} inputProps={{maxLength:6}} sx={{'& .MuiOutlinedInput-root':{borderRadius:2}}}/></Grid>
      </>}
      <Grid item xs={12}>
        <Box display="flex" gap={1} alignItems="flex-end">
          <TextField size="small" label="GPS Latitude" value={form.gpsLat||''} onChange={e=>set('gpsLat',e.target.value)} sx={{flex:1,borderRadius:2}}/>
          <TextField size="small" label="GPS Longitude" value={form.gpsLng||''} onChange={e=>set('gpsLng',e.target.value)} sx={{flex:1}}/>
          <Button variant="outlined" size="small" startIcon={<LocationOn/>} onClick={getLocation} sx={{whiteSpace:'nowrap',borderRadius:2}}>
            Get GPS
          </Button>
        </Box>
      </Grid>
      <RGFField form={form} set={set} label="Landmark" field="landmark" half maxLen={115}/>
      <RGFField form={form} set={set} label="Site Contact Person" field="siteContact" half maxLen={115}/>
      <RGFSelect form={form} set={set} label="Rooftop Type" field="rooftopType" options={['Flat RCC / Concrete','Sloped / Tiled','Metal Sheet','Asbestos Sheet / AC Sheet']}/>
      <RGFSelect form={form} set={set} label="Rooftop Condition" field="rooftopCondition" options={['Excellent — No repairs needed','Good — Minor repairs','Average — Repair before installation','Poor — Major repair required']}/>
      <RGFField form={form} set={set} label="Available Rooftop Area (sq ft)" field="availableRooftopArea" half maxLen={115}/>
      <RGFField form={form} set={set} label="Shadow-Free Area (sq ft)" field="shadowFreeArea" half maxLen={115}/>
      <RGFField form={form} set={set} label="Rooftop Orientation (N/S/E/W/Flat)" field="rooftopOrientation" half maxLen={115}/>
      <RGFField form={form} set={set} label="Rooftop Height (ft)" field="rooftopHeight" half maxLen={115}/>
      <RGFField form={form} set={set} label="Earthing Available? (Yes/No)" field="earthingAvailable" half maxLen={115}/>
      <RGFField form={form} set={set} label="Water Supply for Cleaning? (Yes/No)" field="waterSupplyAvailable" half maxLen={115}/>
      <Grid item xs={12}>
        <Typography fontSize={13} fontWeight={600} color="#003E6F" mb={0.5}>Shading Factors (select all that apply):</Typography>
        <FormGroup row>
          {SHADING.map(s=><FormControlLabel key={s} control={<Checkbox size="small" checked={(form.shadingFactors||[]).includes(s)} onChange={()=>toggleArr('shadingFactors',s)}/>}
            label={<Typography fontSize={12}>{s}</Typography>} sx={{width:{xs:'100%',sm:'50%'}}}/>)}
        </FormGroup>
      </Grid>
      <Grid item xs={12}><TextField fullWidth size="small" label="Obstructions / Shading Observations" value={form.obstructions||''} onChange={e=>set('obstructions',e.target.value)} multiline rows={2} inputProps={{maxLength:500}} sx={{'& .MuiOutlinedInput-root':{borderRadius:2}}}/></Grid>
      <Grid item xs={12}><TextField fullWidth size="small" label="Site Visit Observations / Remarks" value={form.siteRemarks||''} onChange={e=>set('siteRemarks',e.target.value)} multiline rows={3} inputProps={{maxLength:1000}} helperText={`${(form.siteRemarks||'').length}/1000`} sx={{'& .MuiOutlinedInput-root':{borderRadius:2}}}/></Grid>
    </Grid>,

    // Step 3: System
    <Grid container spacing={1.5}>
      <SectionTitle title="Section 4 — Solar System Technical Requirements"/>
      <RGFSelect form={form} set={set} label="System Type *" field="systemType" options={['On-Grid (Net Metering)','Off-Grid (Battery Storage)','Hybrid (Grid + Battery)','Solar Water Heater']}/>
      <RGFField form={form} set={set} label="Proposed System Capacity (kWp)" field="proposedCapacity" half maxLen={115}/>
      <RGFField form={form} set={set} label="Customer Preferred Capacity" field="preferredCapacity" half maxLen={115}/>
      <RGFField form={form} set={set} label="Estimated Panel Count" field="panelCount" half maxLen={115}/>
      <RGFField form={form} set={set} label="Panel Wattage (Wp)" field="panelWattage" half maxLen={115}/>
      <RGFSelect form={form} set={set} label="Panel Technology" field="panelTechnology" options={['Monocrystalline (High Efficiency)','Polycrystalline (Standard)','Bifacial Panels','No Preference']}/>
      <RGFSelect form={form} set={set} label="Inverter Type" field="inverterType" options={['String Inverter','Micro Inverter','Central Inverter','Hybrid Inverter']}/>
      <RGFField form={form} set={set} label="Inverter Brand Preference" field="inverterBrand" half maxLen={115}/>
      <RGFField form={form} set={set} label="Panel Brand Preference" field="panelBrand" half maxLen={115}/>
      <RGFSelect form={form} set={set} label="Mounting Structure" field="mountingType" options={['Fixed Tilt (RCC Roof)','Elevated Structure','Ground Mount','Carport / Parking Shade','Integrated (BIPV)']}/>
      <Grid item xs={12}><Typography fontSize={12} color="#666" fontWeight={600}>Battery (for Off-Grid / Hybrid only):</Typography></Grid>
      <RGFSelect form={form} set={set} label="Battery Type" field="batteryType" options={['Li-ion','Lead Acid','LFP (LiFePO4)','N/A']} half/>
      <RGFField form={form} set={set} label="Backup Duration (hrs)" field="backupDuration" half maxLen={115}/>
      <RGFField form={form} set={set} label="Battery Capacity (kWh)" field="batteryCapacity" half maxLen={115}/>
      <RGFField form={form} set={set} label="Expected Annual Generation (kWh)" field="expectedGeneration" half maxLen={115}/>
      <RGFField form={form} set={set} label="Target Bill Reduction (%)" field="targetBillReduction" half maxLen={115}/>
      <RGFField form={form} set={set} label="Expected Payback Period (years)" field="paybackPeriod" half maxLen={115}/>
      <Grid item xs={12}>
        <FormControlLabel control={<Checkbox checked={form.monitoringRequired} onChange={e=>set('monitoringRequired',e.target.checked)}/>}
          label={<Typography fontSize={13}>Monitoring / SCADA System Required</Typography>}/>
      </Grid>
    </Grid>,

    // Step 4: Financial
    <Grid container spacing={1.5}>
      <SectionTitle title="Section 5 — Financial Details & Subsidy"/>
      <RGFSelect form={form} set={set} label="Installation Category" field="installationCategory" options={['Residential (PM Surya Ghar)','Commercial','Industrial','Agricultural / Farm','Government / Institution']}/>
      <RGFSelect form={form} set={set} label="Subsidy Scheme" field="subsidy" options={['PM Surya Ghar Muft Bijli Yojana','MNRE State Subsidy','NABARD Loan Scheme','KUSUM Scheme (Agriculture)','No Subsidy / Self-Financed']}/>
      <RGFSelect form={form} set={set} label="Payment / Financing Mode" field="paymentMode" options={['Full Cash Payment','Bank Loan (Solar Loan)','NBFC Financing','EMI via Company','Government Scheme Grant']}/>
      <RGFField form={form} set={set} label="Total Estimated Project Cost (₹)" field="totalCost" half maxLen={115}/>
      <RGFField form={form} set={set} label="Subsidy Amount Expected (₹)" field="subsidyAmount" half maxLen={115}/>
      <RGFField form={form} set={set} label="Customer Down Payment (₹)" field="downPayment" half maxLen={115}/>
      <RGFField form={form} set={set} label="Loan Amount Required (₹)" field="loanAmount" half maxLen={115}/>
      <RGFField form={form} set={set} label="Preferred Bank (if loan)" field="preferredBank" half maxLen={115}/>
      <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Bank Account Number" value={form.bankAccount||''} onChange={e=>{if(/^\d{0,20}$/.test(e.target.value))set('bankAccount',e.target.value)}} inputProps={{maxLength:20}} sx={{'& .MuiOutlinedInput-root':{borderRadius:2}}}/></Grid>
      <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="IFSC Code" value={form.ifsc||''} onChange={e=>set('ifsc',e.target.value.toUpperCase().slice(0,11))} inputProps={{maxLength:11}} helperText="11 characters" sx={{'& .MuiOutlinedInput-root':{borderRadius:2}}}/></Grid>
      <RGFField form={form} set={set} label="Bank Branch & Name" field="bankBranch" half maxLen={115}/>
      <RGFField form={form} set={set} label="Monthly Income (₹)" field="monthlyIncome" half maxLen={115}/>
      <RGFField form={form} set={set} label="Annual Income (₹)" field="annualIncome" half maxLen={115}/>
      <RGFField form={form} set={set} label="BPL Card Number (if applicable)" field="bplCardNo" half maxLen={115}/>
      <RGFField form={form} set={set} label="Ration Card Number (if applicable)" field="rationCardNo" half maxLen={115}/>
    </Grid>,

    // Step 5: Documents
    <Grid container spacing={1.5}>
      <SectionTitle title="Section 6 — Documents Collected During Site Visit"/>
      <Grid item xs={12}>
        <Alert severity="info" sx={{fontSize:12,py:0.5}}>Tick all documents physically collected / verified during site visit</Alert>
      </Grid>
      <Grid item xs={12}>
        <FormGroup>
          {DOCS.map(d=><FormControlLabel key={d}
            control={<Checkbox size="small" checked={(form.documentsCollected||[]).includes(d)} onChange={()=>toggleArr('documentsCollected',d)}/>}
            label={<Typography fontSize={13}>{d}</Typography>} sx={{mb:0.5}}/>)}
        </FormGroup>
      </Grid>
      <Grid item xs={12}>
        <Typography fontSize={12} color="#003E6F" fontWeight={600}>
          ✅ {(form.documentsCollected||[]).length} of {DOCS.length} documents collected
        </Typography>
      </Grid>
    </Grid>,

    // Step 6: Load
    <Grid container spacing={1.5}>
      <SectionTitle title="Section 7 — Electrical Load Assessment"/>
      <Grid item xs={12}>
        <Alert severity="info" sx={{fontSize:12,py:0.5}}>Fill appliance details for accurate system sizing</Alert>
      </Grid>
      {(form.loadItems||[]).map((item,i)=>(
        <Grid item xs={12} key={i}>
          <Box sx={{bgcolor:'#F7FAFD',borderRadius:2,p:1.5,border:'1px solid #D0DFF0'}}>
            <Typography fontSize={12} fontWeight={700} color="#003E6F" mb={1}>{item.appliance}</Typography>
            <Grid container spacing={1}>
              {[['qty','Qty'],['wattage','Wattage(W)'],['hoursPerDay','Hrs/Day'],['daysPerMonth','Days/Month']].map(([f,l])=>(
                <Grid item xs={3} key={f}>
                  <TextField size="small" label={l} value={item[f]||''} type="number"
                    onChange={e=>{ const li=[...form.loadItems]; li[i]={...li[i],[f]:e.target.value}; set('loadItems',li); }}
                    sx={{'& input':{fontSize:12},'& label':{fontSize:11}}}/>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Grid>
      ))}
      <RGFField form={form} set={set} label="Total Connected Load (kW)" field="totalConnectedLoad" half/>
      <RGFField form={form} set={set} label="Total Monthly Consumption (kWh)" field="totalMonthlyConsumption" half/>
      <RGFSelect form={form} set={set} label="Peak Demand Period" field="peakDemandPeriod" options={['Morning (6AM-12PM)','Afternoon (12PM-6PM)','Evening (6PM-12AM)','Night (12AM-6AM)','All Day']}/>
    </Grid>,

    // Step 7: Submit
    <Grid container spacing={2}>
      <SectionTitle title="Section 9 — Customer Declaration & Consent"/>
      <Grid item xs={12}>
        <Card sx={{bgcolor:'#FFF8F0',border:'1px solid #E8622A30',borderRadius:2}}>
          <CardContent sx={{p:2}}>
            <Typography fontSize={13} fontWeight={700} color="#003E6F" mb={1}>Customer Declaration — I / We hereby declare that:</Typography>
            {[
              'All information provided is true and correct',
              'I am the legal owner / authorised person for the installation site',
              'I consent to site inspection by Anvil Energy representatives',
              'I agree to sharing of data with DISCOM, subsidy portals, and ERP',
              'I understand subsidy disbursement takes 30–90 days after MNRE approval',
              'I agree to provide all required documents within 7 working days',
              'I consent to Anvil Energy conducting electrical work on my premises',
              'I have been informed about system capacity and expected savings',
            ].map((item,i)=>(
              <Typography key={i} fontSize={12} color="#333" mb={0.5}>✓  {item}</Typography>
            ))}
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12}>
        <FormControlLabel
          control={<Checkbox checked={form.customerConsent} onChange={e=>set('customerConsent',e.target.checked)} color="success"/>}
          label={<Typography fontSize={13} fontWeight={600} color={form.customerConsent?'#1B7A3E':'#B91C1C'}>
            {form.customerConsent ? '✅ Customer has agreed and consented' : '⚠️ Customer consent required — tick to confirm'}
          </Typography>}/>
      </Grid>
      <RGFField form={form} set={set} label="Customer Signature Date" field="customerSignatureDate" type="date" half/>
      <Grid item xs={12}>
        <Box sx={{bgcolor:'#EBF3FB',borderRadius:2,p:2}}>
          <Typography fontSize={13} fontWeight={600} color="#003E6F" mb={1}>📋 Form Summary</Typography>
          <Typography fontSize={12}>Customer: {form.customerName || '—'}</Typography>
          <Typography fontSize={12}>System Type: {form.systemType || '—'}</Typography>
          <Typography fontSize={12}>Proposed Capacity: {form.proposedCapacity || '—'} kWp</Typography>
          <Typography fontSize={12}>Total Cost: ₹{form.totalCost || '—'}</Typography>
          <Typography fontSize={12}>Subsidy: {form.subsidy || '—'}</Typography>
          <Typography fontSize={12}>Documents Collected: {(form.documentsCollected||[]).length} / {DOCS.length}</Typography>
          <Typography fontSize={12}>Salesperson: {user?.name}</Typography>
          <Typography fontSize={12}>Date: {new Date().toLocaleDateString('en-IN')}</Typography>
        </Box>
      </Grid>
    </Grid>,
  ];

  return (
    <Box sx={{p:{xs:1.5,sm:3}, maxWidth:700, mx:'auto'}}>
      {/* Header */}
      <Box display="flex" alignItems="center" gap={1.5} mb={2}>
        <IconButton onClick={()=>navigate('/appointments')} size="small"><ArrowBack/></IconButton>
        <Box>
          <Typography variant="h6" fontWeight={700} color="#003E6F" fontSize={{xs:15,sm:18}}>Solar Installation Form</Typography>
          <Typography fontSize={11} color="text.secondary">ANV-SOLAR-RGF  |  {user?.name}  |  {new Date().toLocaleDateString('en-IN')}</Typography>
        </Box>
        {rgf?.status && <Chip label={rgf.status.replace('_',' ').toUpperCase()} size="small"
          sx={{ml:'auto', bgcolor: rgf.status==='submitted'?'#E8F5EE': rgf.status==='draft'?'#FFF4E0':'#EBF3FB',
          color: rgf.status==='submitted'?'#1B7A3E': rgf.status==='draft'?'#B86A00':'#003E6F', fontWeight:700, fontSize:10}}/>}
      </Box>

      {/* Stepper */}
      <Box sx={{overflowX:'auto', mb:2}}>
        <Stepper activeStep={step} alternativeLabel sx={{'& .MuiStepLabel-label':{fontSize:10}}}>
          {STEPS.map((s,i)=>(
            <Step key={s} completed={i<step} onClick={()=>setStep(i)} sx={{cursor:'pointer'}}>
              <StepLabel>{s}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      {error && <Alert severity="error" sx={{mb:1.5,py:0.5}} onClose={()=>setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{mb:1.5,py:0.5}} onClose={()=>setSuccess('')}>{success}</Alert>}

      {/* Form Content */}
      <Card sx={{borderRadius:2, boxShadow:'0 2px 12px rgba(0,62,111,0.08)'}}>
        <CardContent sx={{p:{xs:1.5,sm:2.5}}}>
          {steps[step]}
        </CardContent>
      </Card>

      {/* Navigation */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mt={2} gap={1}>
        <Button variant="outlined" startIcon={<ArrowBack/>} onClick={()=>setStep(s=>Math.max(0,s-1))}
          disabled={step===0} sx={{borderRadius:2}}>Back</Button>

        <Box display="flex" gap={1}>
          <Button variant="outlined" startIcon={saving?<CircularProgress size={14}/>:<Save/>}
            onClick={saveDraft} disabled={saving} sx={{borderRadius:2,borderColor:'#FCB812',color:'#B86A00'}}>
            {saving?'Saving...':'Save Draft'}
          </Button>

          {step < STEPS.length - 1 ? (
            <Button variant="contained" endIcon={<ArrowForward/>} onClick={()=>setStep(s=>s+1)}
              sx={{bgcolor:'#003E6F',borderRadius:2}}>Next</Button>
          ) : (
            <Button variant="contained" startIcon={submitting?<CircularProgress size={14} sx={{color:'white'}}/>:<Send/>}
              onClick={submitForm} disabled={submitting||!form.customerConsent}
              sx={{bgcolor:'#1B7A3E','&:hover':{bgcolor:'#145E2F'},borderRadius:2}}>
              {submitting?'Submitting...':'Submit Form'}
            </Button>
          )}
        </Box>
      </Box>

      <Typography fontSize={11} color="text.secondary" textAlign="center" mt={1}>
        Step {step+1} of {STEPS.length}  |  Progress: {Math.round((step/STEPS.length)*100)}%
      </Typography>
    </Box>
  );
}
